import { Pagination } from '@/shared/components/Pagination';
import {
  CAlert,
  CBadge,
  CButton,
  CCol,
  CFormSelect,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React from 'react';
import { ACTIVITY_LOG_CATEGORIES, ACTIVITY_LOG_LEVELS } from '../../constants/activityLogFilters';
import type { ActivityLog } from '../../types';
import {
  formatDate,
  formatInitiator,
  formatLevel,
  getEventLabel,
  getLevelBadgeColor,
  getLioboMessage,
  shouldShowDetails,
} from './utils';

type Props = {
  logs: ActivityLog[];
  loading: boolean;
  error: string | null;
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  category: string;
  level: string;
  onPageChange: (p: number) => void;
  onPerPageChange: (pp: number) => void;
  onCategoryChange: (category: string) => void;
  onLevelChange: (level: string) => void;
  onViewDetails: (log: ActivityLog) => void;
};

export const ActivityLogList: React.FC<Props> = ({
  logs,
  loading,
  error,
  page,
  perPage,
  total,
  totalPages,
  category,
  level,
  onPageChange,
  onPerPageChange,
  onCategoryChange,
  onLevelChange,
  onViewDetails,
}) => {
  // Use hardcoded filter options based on backend enums
  const categories = ACTIVITY_LOG_CATEGORIES;
  const levels = ACTIVITY_LOG_LEVELS;

  return (
    <>
      <CRow className="align-items-center mb-3">
        <CCol xs="auto" className="ms-auto">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Category:</small>
              <CFormSelect
                size="sm"
                value={category}
                onChange={(e) => onCategoryChange(e.target.value)}
                style={{ width: 150 }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </CFormSelect>
            </div>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Level:</small>
              <CFormSelect
                size="sm"
                value={level}
                onChange={(e) => onLevelChange(e.target.value)}
                style={{ width: 120 }}
              >
                <option value="">All Levels</option>
                {levels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {formatLevel(lvl)}
                  </option>
                ))}
              </CFormSelect>
            </div>
          </div>
        </CCol>
      </CRow>

      {loading && <div className="text-center">Loading...</div>}

      {error && !loading && <CAlert color="danger">{error}</CAlert>}

      {!loading && !error && logs.length === 0 && (
        <CAlert color="info">No activity found for this customer.</CAlert>
      )}

      {!loading && !error && logs.length > 0 && (
        <>
          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell scope="col">Date and Time</CTableHeaderCell>
                <CTableHeaderCell scope="col">Level</CTableHeaderCell>
                <CTableHeaderCell scope="col">Category</CTableHeaderCell>
                <CTableHeaderCell scope="col">Activity</CTableHeaderCell>
                <CTableHeaderCell scope="col">Initiated By</CTableHeaderCell>
                <CTableHeaderCell scope="col">Details</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {logs.map((log) => (
                <CTableRow key={log.id}>
                  <CTableDataCell>{formatDate(log.createdAt)}</CTableDataCell>
                  <CTableDataCell>
                    <CBadge color={getLevelBadgeColor(log.level)}>{formatLevel(log.level)}</CBadge>
                  </CTableDataCell>

                  <CTableDataCell>{log.category}</CTableDataCell>
                  <CTableDataCell>{getEventLabel(log.event)}</CTableDataCell>
                  <CTableDataCell>{formatInitiator(log.actorType, log.userName)}</CTableDataCell>
                  <CTableDataCell>
                    {(() => {
                      const lioboMessage = getLioboMessage(log);
                      if (lioboMessage) {
                        return (
                          <span title={lioboMessage} style={{ fontSize: '0.875rem' }}>
                            {lioboMessage}
                          </span>
                        );
                      }
                      if (shouldShowDetails(log)) {
                        return (
                          <CButton
                            color="primary"
                            variant="outline"
                            size="sm"
                            onClick={() => onViewDetails(log)}
                          >
                            View
                          </CButton>
                        );
                      }
                      return '-';
                    })()}
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>

          <div className="d-flex justify-content-center mt-3">
            <Pagination
              className="gap-3"
              paginationClassName="mb-0"
              currentPage={page}
              totalPages={totalPages}
              perPage={perPage}
              total={total}
              onPageChange={onPageChange}
              onPerPageChange={onPerPageChange}
            />
          </div>
        </>
      )}
    </>
  );
};
